/**
 * @file components/chat/DocumentMessage.tsx
 * Bulle "Document joint" (utilisateur ➔ plateforme).
 * Affiche le fichier, une barre de progression d'upload temps réel, les cochettes
 * WhatsApp et un badge de sensibilité détectée par le ClassifierCapability.
 */
import React from 'react';
import { FileText, ShieldAlert } from 'lucide-react';
import StatusTicks from './StatusTicks.tsx';
import type { DocumentMessage as DocumentMessageModel } from './types.ts';

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function DocumentMessage({
  message,
}: {
  message: DocumentMessageModel;
}): React.ReactElement {
  const uploading = message.progress < 100;

  return (
    <div className="flex justify-end">
      <div className="w-[78%] max-w-md rounded-2xl rounded-br-sm bg-primary px-3 py-3 text-primary-foreground shadow-sm">
        <div className="flex items-center gap-3 rounded-lg bg-primary-foreground/10 px-3 py-2.5">
          <FileText size={28} className="shrink-0 text-primary-foreground/90" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{message.fileName}</p>
            <p className="text-[11px] text-primary-foreground/70">{message.sizeLabel}</p>
          </div>
        </div>

        {uploading && (
          <div className="mt-2">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-primary-foreground/20">
              <div
                className="h-full rounded-full bg-primary-foreground transition-all duration-200"
                style={{ width: `${message.progress}%` }}
              />
            </div>
            <span className="mt-1 block text-[10px] text-primary-foreground/70">
              Téléversement… {Math.round(message.progress)}%
            </span>
          </div>
        )}

        {message.sensitivity && (
          <div className="badge-c4 mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary-foreground/15 px-2 py-1">
            <ShieldAlert size={12} />
            <span className="text-[11px] font-medium">Classification : {message.sensitivity}</span>
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-1">
          <span className="text-[10px] text-primary-foreground/60">{formatTime(message.at)}</span>
          <StatusTicks status={message.tick} />
        </div>
      </div>
    </div>
  );
}
